import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { LoadingPanel } from "../../LoadingPanel";
import { addNotificationWithTimeout } from '../../../app/addNotificationWithTimeout';
import { handleResponse } from '../../../app/handleResponse';
import { getCSRFToken } from '../../../app/getCSRFToken';
import {
    Grid,
    GridColumn as Column,
} from "@progress/kendo-react-grid";
import { Button } from "@progress/kendo-react-buttons";
import { Checkbox } from "@progress/kendo-react-inputs";

export const AdministratorMyShortedExpensiveEvents = () => {
    const dispatch = useDispatch();

    const [myShortedExpensiveEvents, setMyShortedExpensiveEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [totalEventCount, setTotalEventCount] = useState(0);
    const [isHeaderCheckboxChecked, setIsHeaderCheckboxChecked] = useState(false);
    const [onlyShowBroadcastedEventsFilterValue, setOnlyShowBroadcastedEventsFilterValue] = useState(true);
    const [lastSelectedPOSEventId, setLastSelectedPOSEventId] = useState(undefined);

    const initialPageState = {
        skip: 0,
        take: 20,
    };
    const [page, setPage] = useState(initialPageState);
    const pageChange = (event) => {
        setPage(event.page);
    };

    const getMyShortedExpensiveEvents = (onlyShowBroadcastedEvents) => {
        setIsLoading(true);

        if (onlyShowBroadcastedEvents === undefined) {
            onlyShowBroadcastedEvents = onlyShowBroadcastedEventsFilterValue;
        }

        const dataItem = {
            onlyShowBroadcastedEvents: onlyShowBroadcastedEvents,
        };

        fetch(`/api/administrator/pos/myshortedexpensiveevents`,
            {
                method: "POST",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                setMyShortedExpensiveEvents(result);
                setTotalEventCount(result.length);
                setIsHeaderCheckboxChecked(false);
                setLastSelectedPOSEventId(undefined);
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);
            })
            .finally(() => {
                setIsLoading(false);
            });
    }

    const deleteEvents = (posEventIds) => {
        if (window.confirm("Are you sure you want to delete these events?") === false) {
            return;
        }

        setIsLoading(true);

        const dataItem = {
            posEventIds: posEventIds,
        };

        fetch(`/api/administrator/pos/deleteevents`,
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Events deleted from POS successfully");

                getMyShortedExpensiveEvents();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setIsLoading(false);
            });
    };

    const deleteAndHideEvents = (posEventIds, venueEventKeys) => {
        if (window.confirm("Are you sure you want to delete and hide these events?") === false) {
            return;
        }

        setIsLoading(true);

        const dataItem = {
            posEventIds: posEventIds,
            venueEventKeys: venueEventKeys,
        };

        fetch(`/api/administrator/pos/deleteandhideevents`,
            {
                method: "DELETE",
                headers: {
                    'Content-Type': 'application/json',
                    "X-XSRF-TOKEN": getCSRFToken(),
                },
                body: JSON.stringify(dataItem),
            })
            .then(handleResponse)
            .then(result => {
                addNotificationWithTimeout(dispatch, "success", "Events deleted and permanently hidden successfully");

                getMyShortedExpensiveEvents();
            })
            .catch(error => {
                addNotificationWithTimeout(dispatch, "error", error.message);

                setIsLoading(false);
            });
    };

    const getGridData = () => {
        return myShortedExpensiveEvents.slice(page.skip, page.take + page.skip);
    };

    const ViewUrlCell = (props) => {
        const url = props.dataItem[props.field];

        return (
            <td className={"text-nowrap"}>                
                {url ? <Link to={{ pathname: url }} target={"_blank"} rel={"noopener noreferrer"}>View Website</Link> : ""}
            </td>
        );
    };

    const SelectedCell = (props) => {
        const posEventId = props.dataItem.posEventId;
        const selected = props.dataItem.selected;

        return (
            <td className="text-center">
                <Checkbox onChange={(event) => selectedValueChanged(event, posEventId, !selected)} value={selected} />
            </td>
        );
    };

    const SelectedHeaderCell = () => {
        return (
            <div className="text-center">
                <Checkbox onChange={headerCheckboxValueChanged} value={isHeaderCheckboxChecked} />
            </div>
        );
    };

    const selectedValueChanged = (event, posEventId, selected) => {
        setIsLoading(true);

        setTimeout(() => {
            let myShortedExpensiveEventsCopy = myShortedExpensiveEvents;

            const itemIndex = myShortedExpensiveEventsCopy.findIndex(dataItem =>
                dataItem.posEventId === posEventId
            );

            let isSelectingMultipleItems = false;

            //Shift selection of multiple items            
            if (event.nativeEvent.shiftKey
                && lastSelectedPOSEventId !== undefined
                && lastSelectedPOSEventId !== posEventId) {
                const lastSelectedItemIndex = myShortedExpensiveEventsCopy.findIndex(dataItem =>
                    dataItem.posEventId === lastSelectedPOSEventId
                );

                if (lastSelectedItemIndex >= 0) {
                    isSelectingMultipleItems = true;

                    let lowIndex = -1;
                    let highIndex = -1;

                    if (itemIndex > lastSelectedItemIndex) {
                        lowIndex = lastSelectedItemIndex;
                        highIndex = itemIndex;
                    }
                    else {
                        lowIndex = itemIndex;
                        highIndex = lastSelectedItemIndex;
                    }

                    for (let i = lowIndex; i <= highIndex; i++) {
                        myShortedExpensiveEventsCopy[i].selected = selected;
                    }
                }
            }

            if (isSelectingMultipleItems === false) {
                myShortedExpensiveEventsCopy[itemIndex].selected = selected;
            }

            if (selected === true) {
                setLastSelectedPOSEventId(posEventId);
            }

            setMyShortedExpensiveEvents(myShortedExpensiveEventsCopy);

            setIsHeaderCheckboxChecked(false);
            setIsLoading(false);
        }, 1);
    };

    const headerCheckboxValueChanged = () => {
        setIsLoading(true);

        setTimeout(() => {
            const checked = !isHeaderCheckboxChecked;
            setIsHeaderCheckboxChecked(checked);

            let myShortedExpensiveEventsCopy = myShortedExpensiveEvents;
            
            myShortedExpensiveEventsCopy.forEach(dataItem =>
                dataItem.selected = false
            );

            if (checked === true) {
                const gridData = getGridData();

                myShortedExpensiveEventsCopy.forEach(dataItem => {
                    const exists = gridData.includes(dataItem);

                    if (exists) {
                        dataItem.selected = true
                    }
                });
            }      

            setMyShortedExpensiveEvents(myShortedExpensiveEventsCopy);

            setLastSelectedPOSEventId(undefined);

            setIsLoading(false);
        }, 1);
    };

    const deleteSelectedEvents = () => {
        let posEventIds = [];

        myShortedExpensiveEvents.forEach(dataItem => {
            if (dataItem.selected === true) {
                posEventIds.push(dataItem.posEventId);
            }
        });

        if (posEventIds.length === 0) {
            addNotificationWithTimeout(dispatch, "error", "No events selected");
            return;
        }

        deleteEvents(posEventIds);
    };

    const deleteAndHideSelectedEvents = () => {
        let posEventIds = [];
        let venueEventKeys = [];

        myShortedExpensiveEvents.forEach(dataItem => {
            if (dataItem.selected === true) {
                posEventIds.push(dataItem.posEventId);

                if (dataItem.venueEventKey) {
                    venueEventKeys.push(dataItem.venueEventKey);
                }
            }
        });

        if (posEventIds.length === 0) {
            addNotificationWithTimeout(dispatch, "error", "No events selected");
            return;
        }

        deleteAndHideEvents(posEventIds, venueEventKeys);
    };

    const onlyShowBroadcastedEventsFilterValueChanged = (event) => {
        const value = event.value;

        setOnlyShowBroadcastedEventsFilterValue(value);
        getMyShortedExpensiveEvents(value);
    }

    useEffect(() => {
        document.title = "My Shorted Expensive Events";

        getMyShortedExpensiveEvents();
    }, []);

    return (
        <div className="administratorReport">
            {isLoading ? <LoadingPanel /> : <></>}

            <div className="k-form k-form-md">
                <div className="k-form-fieldset container">
                    <div className="row">
                        <div className="col-12">
                            <div className="k-form-field">
                                <Checkbox
                                    label={"Only show broadcasted events"}
                                    onChange={onlyShowBroadcastedEventsFilterValueChanged}
                                    value={onlyShowBroadcastedEventsFilterValue}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="m-2 k-form-buttons">
                <Button type={"button"} icon="trash" title="Delete Selected" onClick={deleteSelectedEvents}>
                    Delete Selected
                </Button>
                
                <Button type={"button"} icon="trash" title="Delete and Permanently Hide Selected Events" onClick={deleteAndHideSelectedEvents}>
                    Delete and Permanently Hide Selected Events
                </Button>
            </div>

            <Grid
                data={getGridData()}
                resizable={true}
                skip={page.skip}
                take={page.take}
                total={totalEventCount}
                pageable={true}
                onPageChange={pageChange}
                scrollable="none"
            >
                <Column cell={SelectedCell} headerCell={SelectedHeaderCell} />
                <Column field="ticketingUrl" title={"Website"} cell={ViewUrlCell} />
                <Column field="cost" title={"Cost"} />
                <Column field="price" title={"Price"} />
                <Column field="ticketingWebsiteName" title={"Website"} />
                <Column field="eventName" title={"Event"} />
                <Column field="venueName" title={"Venue"} />
                <Column field="eventDateAsString" title={"Date"} />
                <Column field="venueEventStatusTypeName" title={"Status"} />
                <Column field="isBroadcasted" title={"Broadcasted"} />
            </Grid>
        </div>
    );
}